import React, { useState } from 'react'
import { generatePath, Link, NavLink, useParams } from 'react-router-dom'

import { AddOutline, ArrowBackOutline } from 'react-ionicons'
import { Button, FlexboxGrid, Nav, Navbar, Panel, Table } from 'rsuite'

import SettingsTopMenu from '@components/settings/SettingsTopMenu'

import { 
  ROUTE_SETTINGS_MENUS,
  ROUTE_SETTINGS_MENU_ITEMS_CREATE,
  ROUTE_SETTINGS_MENU_ITEMS_EDIT 
} from '@@partner:config/routes'

const { Column, HeaderCell, Cell } = Table

// Временные типы
interface MenuItem {
  id: string
  menu_id: string
  internal_name: string
  title: string
  icon_code?: string
  icon_color?: string
  text_color?: string
  link_url?: string
  link_type: string
  is_active: boolean
  sort_order: number
}

interface MenuItemsPageParams {
  menuId: string
}

// Временные данные
const mockMenuItems: MenuItem[] = [
  {
    id: '1',
    menu_id: '1',
    internal_name: 'catalog',
    title: 'Каталог',
    icon_code: 'grid-outline',
    icon_color: '#007bff',
    text_color: '#333333',
    link_url: '/catalog',
    link_type: 'internal',
    is_active: true,
    sort_order: 1
  },
  {
    id: '2',
    menu_id: '1',
    internal_name: 'delivery',
    title: 'Доставка',
    icon_code: 'car-outline',
    icon_color: '#28a745',
    text_color: '#333333',
    link_url: '/delivery',
    link_type: 'internal',
    is_active: true,
    sort_order: 2
  }
]

const LINK_TYPE_LABELS = {
  internal: 'Внутренняя',
  external: 'Внешняя',
  category: 'Категория',
  product: 'Товар'
}

export default function MenuItemsPage () {
  const { menuId } = useParams<MenuItemsPageParams>()
  const [ loading ] = useState(false)

  // TODO: Загрузить элементы меню по menuId
  const menuItems = mockMenuItems.filter(item => item.menu_id === menuId)

  return (
    <div className="MenuItemsPage">
      <h1 className="u-heading">Элементы меню</h1>
      <SettingsTopMenu />
      
      <Navbar appearance='subtle'>
        <Nav>
          <Nav.Item
            icon={<ArrowBackOutline />}
            as={NavLink}
            to={ROUTE_SETTINGS_MENUS}
          >
            Назад к списку меню
          </Nav.Item>
          <Nav.Item
            icon={<AddOutline />}
            as={NavLink}
            to={generatePath(ROUTE_SETTINGS_MENU_ITEMS_CREATE, { menuId })}
          >
            Добавить элемент
          </Nav.Item>
        </Nav>
      </Navbar>

      <Panel shaded>
        <Table
          loading={loading}
          data={menuItems}
          autoHeight
          rowHeight={60}
        >
          <Column width={60} align="center">
            <HeaderCell>№</HeaderCell>
            <Cell dataKey="sort_order" />
          </Column>

          <Column width={150}>
            <HeaderCell>Внутреннее имя</HeaderCell>
            <Cell>
              {(rowData: MenuItem) => (
                <code className="bg-gray-100 px-2 py-1 rounded text-sm">
                  {rowData.internal_name}
                </code>
              )}
            </Cell>
          </Column>

          <Column width={150} flexGrow={1}>
            <HeaderCell>Название</HeaderCell>
            <Cell dataKey="title" />
          </Column>

          <Column width={100} align="center">
            <HeaderCell>Иконка</HeaderCell>
            <Cell>
              {(rowData: MenuItem) => (
                rowData.icon_code ? (
                  <div 
                    className="flex items-center justify-center"
                    style={{ color: rowData.icon_color }}
                  >
                    <code className="text-xs">{rowData.icon_code}</code>
                  </div>
                ) : (
                  <span className="text-gray-400">—</span>
                )
              )}
            </Cell>
          </Column>

          <Column width={120}>
            <HeaderCell>Тип ссылки</HeaderCell>
            <Cell>
              {(rowData: MenuItem) => (
                <span className="px-2 py-1 rounded text-sm bg-blue-100 text-blue-800">
                  {LINK_TYPE_LABELS[rowData.link_type as keyof typeof LINK_TYPE_LABELS] || rowData.link_type}
                </span>
              )}
            </Cell>
          </Column>

          <Column width={200}>
            <HeaderCell>URL</HeaderCell>
            <Cell>
              {(rowData: MenuItem) => (
                <code className="text-xs text-gray-600">
                  {rowData.link_url || '—'}
                </code>
              )}
            </Cell>
          </Column>

          <Column width={100} align="center">
            <HeaderCell>Статус</HeaderCell>
            <Cell>
              {(rowData: MenuItem) => (
                <span className={`px-2 py-1 rounded text-sm ${
                  rowData.is_active 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {rowData.is_active ? 'Активен' : 'Неактивен'}
                </span>
              )}
            </Cell>
          </Column>

          <Column width={120} align="center" fixed="right">
            <HeaderCell>Действия</HeaderCell>
            <Cell>
              {(rowData: MenuItem) => (
                <FlexboxGrid justify="center" align="middle">
                  <Button
                    size="xs"
                    appearance="link"
                    as={Link}
                    to={generatePath(ROUTE_SETTINGS_MENU_ITEMS_EDIT, { 
                      menuId, 
                      itemId: rowData.id 
                    })}
                  >
                    Редактировать
                  </Button>
                </FlexboxGrid>
              )}
            </Cell>
          </Column>
        </Table>
      </Panel>
    </div>
  )
}
